import React from "react";
import { Link } from "react-router-dom";
import { vibesColors as colors } from "../../theme/vibesColors";
import { PLACEHOLDER_BANNER_URL } from "../../constants/mediaPlaceholders";
import FavoriteAppUnlockButton from "../common/FavoriteAppUnlockButton";
import {
  getBottleServiceHeroImageUrl,
  pickEventType,
  pickId,
  pickImageUrl,
  pickTitle,
  isBottleServiceEvent,
  isVipEntryEvent,
  resolvePublicAssetUrl,
} from "../../utils/clubDetailHelpers";
import { formatEventDateLabel } from "../../utils/eventDetailHelpers";

export default function ClubEventsList({ events, coverImageUrl, hostId = "" }) {
  const pickCardImage = (ev, isBottleService) => {
    if (!ev || typeof ev !== "object") return "";
    if (isBottleService) {
      const bottleThumb = pickImageUrl(ev);
      return bottleThumb || getBottleServiceHeroImageUrl();
    }

    const direct = pickImageUrl(ev);
    if (direct) return direct;

    const fromArrayFirst = (arr) => {
      if (!Array.isArray(arr) || !arr.length) return "";
      const first = arr[0];
      if (typeof first === "string") return resolvePublicAssetUrl(first);
      if (first && typeof first === "object") {
        const nested =
          first.image ||
          first.imageUrl ||
          first.thumbnail ||
          first.photo ||
          first.url ||
          first.uri ||
          "";
        return nested ? resolvePublicAssetUrl(String(nested)) : "";
      }
      return "";
    };

    const fromBooths = fromArrayFirst(ev.booths);
    if (fromBooths) return fromBooths;
    const fromTickets = fromArrayFirst(ev.tickets);
    if (fromTickets) return fromTickets;
    const fromImages = fromArrayFirst(ev.images);
    if (fromImages) return fromImages;
    const fromGallery = fromArrayFirst(ev.gallery);
    if (fromGallery) return fromGallery;

    const raw =
      ev.eventImage ||
      ev.mainImage ||
      ev.bannerImage ||
      ev.poster ||
      ev.thumbnailImage ||
      ev.coverImage ||
      "";
    return raw ? resolvePublicAssetUrl(String(raw)) : "";
  };

  if (!events.length) {
    return (
      <p className="mb-0" style={{ color: colors.textColor }}>
        No events for this filter.
      </p>
    );
  }

  return (
    <ul className="list-unstyled row g-3 g-lg-4 mb-0">
      {events.map((ev) => {
        const eid = pickId(ev);
        if (!eid) return null;
        const evTitle = pickTitle(ev);
        const startDateRaw = ev?.startDate || ev?.eventStartDate || ev?.date || "";
        const startDateLabel = formatEventDateLabel(startDateRaw);
        const typ = pickEventType(ev) || "Event";
        const normalizedType = String(typ).trim().toLowerCase();
        const isVipEntry = isVipEntryEvent(ev);
        const isBottleService = isBottleServiceEvent(ev);
        const isTableCategory = normalizedType === "table";
        const targetPath = isBottleService
          ? hostId
            ? `/bottle/${encodeURIComponent(eid)}?host=${encodeURIComponent(hostId)}`
            : `/bottle/${encodeURIComponent(eid)}`
          : hostId
            ? `/event/${encodeURIComponent(eid)}?host=${encodeURIComponent(hostId)}`
            : `/event/${encodeURIComponent(eid)}`;
        const thumb = pickCardImage(ev, isBottleService);
        const cardImage = thumb || coverImageUrl || PLACEHOLDER_BANNER_URL;
        const typeBadgeLabel =
          normalizedType === "vip entry" || normalizedType === "vipentry"
            ? "VIP Entry"
            : typ;
        return (
          <li key={eid} className="col-12">
            <div
              className="h-100 rounded-4 p-3 d-flex flex-column"
              style={{
                background: colors.cardBackground,
                border: `1px solid ${colors.violate}44`,
                minHeight: "100%",
              }}
            >
              {isTableCategory ? (
                <div className="d-flex gap-3 flex-grow-1">
                  <div
                    className="flex-shrink-0 rounded-3 overflow-hidden position-relative"
                    style={{
                      width: 100,
                      height: 88,
                      background: "#1a0a2e",
                      backgroundImage: cardImage ? `url(${cardImage})` : undefined,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                    }}
                  >
                    <span
                      className="position-absolute top-0 start-0 m-2 px-2 py-1 rounded small fw-bold"
                      style={{
                        fontSize: "0.65rem",
                        background: colors.BtnBackground,
                        color: colors.white,
                      }}
                    >
                      {typeBadgeLabel}
                    </span>
                  </div>
                  <div
                    className="flex-grow-1 d-flex flex-column"
                    style={{ minWidth: 0 }}
                  >
                    <div
                      className="fw-bold mb-1"
                      style={{ color: colors.white, fontSize: "0.98rem" }}
                    >
                      {evTitle}
                    </div>
                    <p
                      className="small mb-0 mt-1"
                      style={{
                        color: colors.textColor,
                        lineHeight: 1.35,
                      }}
                    >
                      {ev.details || ev.description || ""}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="d-flex justify-content-between align-items-center gap-3 flex-grow-1">
                  <div className="d-flex flex-column gap-1" style={{ minWidth: 0 }}>
                    <div className="fw-bold" style={{ color: colors.white, fontSize: "1rem" }}>
                      {evTitle}
                    </div>
                    {!isBottleService ? (
                      <div className="small" style={{ color: colors.lightBackground }}>
                        {startDateLabel}
                      </div>
                    ) : null}
                  </div>
                  <span
                    className="px-2 py-1 rounded-pill small fw-bold"
                    style={{
                      fontSize: "0.65rem",
                      background: colors.BtnBackground,
                      color: colors.white,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {typeBadgeLabel}
                  </span>
                </div>
              )}
              <div className="d-flex justify-content-end align-items-center gap-2 mt-2 pt-1">
                <FavoriteAppUnlockButton variant="row" ariaLabel={`Favorite: ${evTitle}`} />
                <Link
                  to={targetPath}
                  state={{ fromClubList: true }}
                  className="d-inline-flex align-items-center justify-content-center rounded-circle text-decoration-none"
                  style={{
                    width: 36,
                    height: 36,
                    background: colors.BtnBackground,
                    color: colors.white,
                    fontWeight: 700,
                  }}
                  aria-label={`View details: ${evTitle}`}
                >
                  →
                </Link>
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
